import type {
  FreshStrawPartnershipShare,
  FreshStrawStageGradeEntry,
} from '@/app/services/freshStrawRest';

import type { FreshStrawRow } from '../FreshStrawActionPanel/payload';

/**
 * Thin shape of a fertilization row. Matches the fields we care about on
 * `FertilizationResponse` from the generated EmbryoService client, plus
 * derived fields we compute on the UI (entryTotal).
 */
export interface FertilizationLike {
  fertilizationId?: string | null;
  mating?: string | null;
  donorId?: string | null;
  donorName?: string | null;
  origShipped?: number | null;
  clientShipped?: number | null;
  clientTransfer?: number | null;
  clientFrozen?: number | null;
  clientDiscarded?: number | null;
  /** Used by the "Add discards" bulk action to compute the degenerate count. */
  heldTransfer?: number | null;
  heldFrozen?: number | null;
  gradeOneDiscarded?: number | null;
  gradeTwoDiscarded?: number | null;
  gradeThreeDiscarded?: number | null;
  ivcNumber?: number | null;
  cleaved?: number | null;
  degenerates?: number | null;
  inStraws?: number | null;
  partnership?: FreshStrawPartnershipShare[] | null;
}

/**
 * Build a short, display-ready string like "ACCT1 50% / ACCT2 50%" for the
 * partnership summary cell. Prefers account code over name for density.
 * Returns null when there are no populated shares (no chip rendered).
 */
export function formatPartnershipSummary(
  partnership: FreshStrawPartnershipShare[] | null | undefined,
): string | null {
  if (!partnership || partnership.length === 0) return null;
  const parts = partnership
    .filter((s) => (s?.percent ?? 0) > 0 && (s?.accountCode || s?.accountName))
    .map((s) => {
      const label = s.accountCode || s.accountName || '???';
      const pct = Math.round(s.percent ?? 0);
      return `${label} ${pct}%`;
    });
  return parts.length > 0 ? parts.join(' / ') : null;
}

/**
 * Legacy MVC's "add discards" derivation (`FreshStrawActionBar.handleAddDiscards`):
 * a row's degenerates = origShipped − (heldTransfer + heldFrozen + grade1 + grade2 + grade3).
 * Rows with `origShipped` <= 0 are skipped to match legacy behavior.
 */
export function computeDegenerates(
  row: FertilizationLike,
): { fertilizationId: string; degenerates: number } | null {
  const shipped = row.origShipped ?? 0;
  if (shipped <= 0) return null;
  const fertId = row.fertilizationId;
  if (!fertId) return null;

  const degen =
    shipped -
    ((row.heldTransfer ?? 0) +
      (row.gradeOneDiscarded ?? 0) +
      (row.gradeTwoDiscarded ?? 0) +
      (row.gradeThreeDiscarded ?? 0) +
      (row.heldFrozen ?? 0));

  return { fertilizationId: fertId, degenerates: degen };
}

/** Count of embryos entered via the stage/grade dialog for a given row. */
export function entryTotal(entries: FreshStrawStageGradeEntry[]): number {
  return entries.reduce((sum, e) => sum + (e.count || 0), 0);
}

/** Toggle a row in the selected list, preserving per-row entries on re-select. */
export function toggleSelection(
  selected: FreshStrawRow[],
  fertilizationId: string,
): FreshStrawRow[] {
  const existing = selected.find((r) => r.fertilizationId === fertilizationId);
  if (existing) {
    return selected.filter((r) => r.fertilizationId !== fertilizationId);
  }
  return [...selected, { fertilizationId, entries: [] }];
}

/** Replace (or insert) a row's stage/grade entries. */
export function upsertRowEntries(
  selected: FreshStrawRow[],
  fertilizationId: string,
  entries: FreshStrawStageGradeEntry[],
): FreshStrawRow[] {
  const existing = selected.find((r) => r.fertilizationId === fertilizationId);
  if (existing) {
    return selected.map((r) =>
      r.fertilizationId === fertilizationId ? { ...r, entries } : r,
    );
  }
  return [...selected, { fertilizationId, entries }];
}

/** Row found in the selected list, or a fresh empty row. */
export function getOrInitRow(
  selected: FreshStrawRow[],
  fertilizationId: string,
): FreshStrawRow {
  return (
    selected.find((r) => r.fertilizationId === fertilizationId) ?? {
      fertilizationId,
      entries: [],
    }
  );
}
